"use client";

import Image from "next/image";
import { Link } from "@/navigation";
import { useTranslations } from "next-intl";

// Globe 图标组件
const GlobeIcon = ({ className }: { className?: string }) => (
	<svg
		className={`h-14 w-14 sm:h-16 sm:w-16 md:h-[72px] md:w-[72px] lg:h-20 lg:w-20 ${
			className ?? "text-[#0d68ea]"
		}`}
		viewBox="0 0 48 48"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<circle cx="24" cy="24" r="20" />
		<ellipse cx="24" cy="24" rx="20" ry="8" />
		<ellipse cx="24" cy="24" rx="8" ry="20" />
	</svg>
);

// 45度倾斜箭头图标组件
const ArrowIcon = () => (
	<svg
		className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
		viewBox="0 0 16 16"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
	>
		<path d="M4 12L12 4M12 4H6M12 4V10" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);

// 统计数据配置
const STATS_KEYS = [
	{ valueKey: "stat1Value", labelKey: "stat1Label" },
	{ valueKey: "stat2Value", labelKey: "stat2Label" },
	{ valueKey: "stat3Value", labelKey: "stat3Label" },
] as const;

export default function AxisMachineShop() {
	const t = useTranslations("fiveAxisMachineShop");

	return (
		<section className="bg-white pb-8 sm:pb-10 md:pb-12 lg:pb-16">
			<div className="container">
				{/* 标题行：文字 + Globe 图标 */}
				<div className="relative mb-8 sm:mb-10 md:mb-12">
					<h2 className="flex flex-col gap-1 text-2xl font-bold leading-tight text-black sm:mb-5 sm:text-[30px] md:mb-6 md:text-4xl">
						<span>{t("title1")}</span>
						<span>{t("title2")}</span>
					</h2>
					<div className="absolute left-[45%] top-1/2 -translate-x-1/2 -translate-y-1/2">
						<GlobeIcon className="!text-themePrimary400" />
					</div>
				</div>

				{/* 图片 + 内容并排 */}
				<div className="flex flex-col gap-8 lg:flex-row lg:items-stretch lg:gap-12 xl:gap-16">
					{/* 左侧图片 */}
					<div className="w-full lg:w-[45%]">
						<div className="relative aspect-[4/3] w-full overflow-hidden shadow-lg lg:aspect-auto lg:h-full lg:min-h-[480px]">
							<Image
								src="/image/new-version/machining/five-axis/factory/js-precision-5-axis-machine-shop.jpg"
								alt={t("imageAlt")}
								fill
								className="object-cover"
								sizes="(max-width: 1024px) 100vw, 45vw"
							/>
						</div>
					</div>

					{/* 右侧内容区 */}
					<div className="flex w-full flex-col justify-center lg:w-[55%]">
						{/* 副标题 */}
						<h3 className="mb-4 text-lg font-bold text-black sm:text-xl md:text-2xl">{t("subtitle")}</h3>

						{/* 描述文本 */}
						<p className="mb-8 text-sm leading-relaxed text-black md:text-base">{t("description")}</p>

						{/* 按钮组 */}
						<div className="mb-8 flex flex-wrap items-center gap-4">
							{/* Get Instant Quote 按钮 */}
							<Link
								href="/upload"
								className="inline-flex items-center justify-center rounded bg-themePrimary400 px-5 py-3 text-sm font-medium text-white transition-all duration-300 hover:bg-opacity-80 hover:text-white md:px-6 md:py-3.5 md:text-base"
							>
								{t("getQuote")}
							</Link>

							{/* Tour 链接 - 绿色文字带45度箭头 */}
							<Link
								href="#factory-video"
								className="group inline-flex items-center gap-2 text-sm font-medium text-themePrimary400 transition-colors hover:text-mainColor md:text-base"
							>
								{t("tourLink")}
								<ArrowIcon />
							</Link>
						</div>

						{/* 统计数据 - 带下划线样式 */}
						<div className="flex flex-wrap justify-between gap-8 border-t border-gray-300 pt-6 md:gap-12 lg:gap-16">
							{STATS_KEYS.map((stat, index) => (
								<div key={index} className="flex flex-col">
									<span className="text-2xl font-bold text-black sm:text-3xl md:text-4xl">
										{t(stat.valueKey)}
									</span>
									<span className="mt-2 text-sm text-black">{t(stat.labelKey)}</span>
								</div>
							))}
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
